import { useState, type ChangeEvent } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { fmtInt } from '../../lib/format';
import type { Column } from '../../lib/columns';
import { useConfig } from '../../store/config';

const STORY_COLOR = '#2e7d32';
const BUG_COLOR   = '#c62828';
const KAPA_COLOR  = '#5e7186';

type Props = {
  col: Column;
  scaleMax: number;     // gemeinsame Y-Skala über alle Säulen einer Card
  railHeight?: number;  // Default 160
};

// Eine vertikale Säule: zwei gestapelte Segmente (Bugs unten rot, Stories
// oben grün), Total-Zahl darüber, Label + Sub-Label darunter, am Fuß
// entweder Werte (read-only) oder Inputs (editierbar = initial/kapaPlan).
export function ColumnBar({ col, scaleMax, railHeight = 160 }: Props) {
  const setInitialPlan   = useConfig(s => s.setInitialPlan);
  const clearInitialPlan = useConfig(s => s.clearInitialPlan);

  const [edit, setEdit] = useState<{ s: string; b: string } | null>(null);

  const sRound = Math.round(col.stories);
  const bRound = Math.round(col.bugs);
  const total = sRound + bRound;
  const denom = Math.max(1, scaleMax);
  const sH = total > 0 ? (sRound / denom) * railHeight : 0;
  const bH = total > 0 ? (bRound / denom) * railHeight : 0;

  function commit(s: number, b: number) {
    if (!col.initKey) return;
    if (s <= 0 && b <= 0) void clearInitialPlan(col.initKey);
    else void setInitialPlan(col.initKey, s, b);
    setEdit(null);
  }

  function onChange(field: 's' | 'b', e: ChangeEvent<HTMLInputElement>) {
    setEdit(prev => {
      const base = prev ?? { s: String(sRound || ''), b: String(bRound || '') };
      return { ...base, [field]: e.target.value };
    });
  }

  function onCommit() {
    if (!edit) return;
    const s = parseFloat(edit.s.replace(',', '.')) || 0;
    const b = parseFloat(edit.b.replace(',', '.')) || 0;
    commit(s, b);
  }

  return (
    <Tooltip arrow title={<Box sx={{ whiteSpace: 'pre-line', fontSize: 12, maxWidth: 360 }}>{col.tooltip}</Box>}>
      <Box
        sx={{
          flex: 1,
          minWidth: 110,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'help',
        }}
      >
        {/* Total-Zahl */}
        <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
          {fmtInt(total)}
        </Typography>

        {/* Bar */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 90,
            height: railHeight,
            bgcolor: 'action.hover',
            borderRadius: 1.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
          }}
        >
          {bH > 0 && (
            <Box sx={{ height: bH, bgcolor: BUG_COLOR, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
              {bH > 18 ? fmtInt(bRound) : ''}
            </Box>
          )}
          {sH > 0 && (
            <Box sx={{ height: sH, bgcolor: STORY_COLOR, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
              {sH > 18 ? fmtInt(sRound) : ''}
            </Box>
          )}
          {col.key === 'kapa' && total > 0 && bH === 0 && sH === 0 && (
            <Box sx={{ height: railHeight * (total / denom), bgcolor: KAPA_COLOR }} />
          )}
        </Box>

        {/* Label */}
        <Box sx={{ textAlign: 'center', mt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.1 }}>
            {col.label}
          </Typography>
          {col.subLabel && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: 10, mt: 0.25 }}>
              {col.subLabel}
            </Typography>
          )}
          {col.isDefault && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: 10, fontStyle: 'italic' }}>
              Default = Kapa · überschreibbar
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ width: '100%', mt: 0.5 }}>
          {col.editable && col.initKey ? (
            <Stack spacing={0.5}>
              <TextField
                size="small"
                label="Stories"
                type="number"
                value={edit ? edit.s : (sRound || '')}
                onChange={(e) => onChange('s', e as ChangeEvent<HTMLInputElement>)}
                onBlur={onCommit}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                slotProps={{ htmlInput: { min: 0, step: 1, style: { textAlign: 'right', fontSize: 12, padding: '4px 8px' } } }}
              />
              <TextField
                size="small"
                label="Bugs"
                type="number"
                value={edit ? edit.b : (bRound || '')}
                onChange={(e) => onChange('b', e as ChangeEvent<HTMLInputElement>)}
                onBlur={onCommit}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                slotProps={{ htmlInput: { min: 0, step: 1, style: { textAlign: 'right', fontSize: 12, padding: '4px 8px' } } }}
              />
            </Stack>
          ) : total === 0 ? (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', fontStyle: 'italic' }}>
              keine Werte
            </Typography>
          ) : (
            <Stack spacing={0.25}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STORY_COLOR }} />
                <Typography variant="caption">Stories <b>{fmtInt(col.stories)}</b></Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BUG_COLOR }} />
                <Typography variant="caption">Bugs <b>{fmtInt(col.bugs)}</b></Typography>
              </Stack>
            </Stack>
          )}
        </Box>

        {/* Daten-Qualitäts-Warnung wenn < 50 % der Items Estimate haben */}
        {col.qualityWarning && (
          <Box
            sx={{
              width: '100%',
              mt: 1,
              p: 1,
              borderRadius: 1,
              border: 1,
              borderColor: '#ed6c02',
              bgcolor: '#fff4e5',
              color: '#663c00',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                fontSize: 9,
                mb: 0.5,
              }}
            >
              ⚠️ Daten-Qualität niedrig
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: 10.5, lineHeight: 1.35 }}>
              {col.qualityWarning}
            </Typography>
          </Box>
        )}

        {/* Permanente Erläuterung — was wird gezählt, welche Status, etc. */}
        {col.detailsLines && col.detailsLines.length > 0 && (
          <Box
            sx={{
              width: '100%',
              mt: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                fontSize: 9,
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              Was wird gezählt?
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 1.5, '& > li': { mb: 0.25 } }}>
              {col.detailsLines.map((line, i) => (
                <Typography
                  key={i}
                  component="li"
                  variant="caption"
                  sx={{ display: 'list-item', color: 'text.secondary', fontSize: 10, lineHeight: 1.35 }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
